import { useEffect, useRef } from "react";

import {
  type QueryFunction,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import axios from "axios";

import { toast, toastError } from "@alpacahq/ui/old";

import { MustReauthenticateError } from "../auth/MustReauthenticateError";
import { getErrorMessage } from ".";

export type QueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, QueryKey>,
  "queryKey" | "queryFn"
>;

/**
 * Retry predicate for the account-list queries. Never retries an auth failure —
 * a 401 (for a `logoutOn401` probe a retry would re-run the logout interceptor;
 * for the others it is just noise) or a request-interceptor
 * `MustReauthenticateError` (logout is already in flight) — but keeps
 * react-query's default of up to 3 retries for transient failures.
 */
export const doNotRetryOn401 = (
  failureCount: number,
  error: unknown,
): boolean =>
  !(error instanceof MustReauthenticateError) &&
  !(axios.isAxiosError(error) && error.response?.status === 401) &&
  failureCount < 3;

/**
 * `meta` flag read by `useRequest` to opt out of the shared error toast.
 * Pass `meta: { showErrorToast: false }` when the call site handles errors
 * itself.
 */
export const REQUEST_ERROR_TOAST_META = "showErrorToast" as const;

/**
 * Shared query-error toast, fired from `useRequest`'s own effect rather than
 * a global `QueryCache` handler — `Query.meta` is shared across observers of
 * a queryKey, so a cache-level handler can't tell two call sites with
 * different opt-in/opt-out preferences apart.
 */
export const handleRequestQueryError = (
  err: Error,
  queryKey: QueryKey,
): void => {
  // todo: suppress account errors for /trade auth check (for now)
  if (Array.isArray(queryKey) && queryKey.includes("account")) {
    return;
  }
  const key = "errorToastKey";
  if (toast.isActive(key)) {
    return;
  }
  const message = getErrorMessage(err);

  toastError(message, {
    toastId: key,
    autoClose: 3000,
    position: "top-right",
  });
};

export function useRequest<T>(
  queryKey: QueryKey,
  queryFn: QueryFunction<T>,
  options: QueryOptions<T> = {},
): UseQueryResult<T, Error> {
  const result = useQuery({
    queryKey,
    queryFn,
    ...options,
  });

  const showErrorToast = options.meta?.[REQUEST_ERROR_TOAST_META] !== false;
  // undefined (not the current errorUpdatedAt) so an already-failed query is
  // still toasted once on mount.
  const handledErrorAt = useRef<number>();

  useEffect(() => {
    if (
      !showErrorToast ||
      !result.error ||
      !result.errorUpdatedAt ||
      handledErrorAt.current === result.errorUpdatedAt
    ) {
      return;
    }
    handledErrorAt.current = result.errorUpdatedAt;
    handleRequestQueryError(result.error, queryKey);
  }, [result.error, result.errorUpdatedAt, showErrorToast, queryKey]);

  return result;
}

export default useRequest;
